import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "lifeos_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

const http = axios.create({ baseURL: API });
http.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});
http.interceptors.response.use(
  (r) => r,
  (err) => {
    // Normalize FastAPI 422 validation errors: detail is an array of objects,
    // which crashes React if passed to a toast/child. Flatten to a string.
    const d = err.response?.data?.detail;
    if (Array.isArray(d)) {
      err.response.data.detail = d.map((e) => (e && typeof e === "object" ? e.msg || JSON.stringify(e) : String(e))).join(" · ");
    } else if (d && typeof d === "object") {
      err.response.data.detail = d.msg || JSON.stringify(d);
    }
    const url = err.config?.url || "";
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/google/session");
    if (err.response?.status === 401 && !isAuthCall) {
      setToken(null);
      toast.error("Session expired — please log in again.");
      window.dispatchEvent(new CustomEvent("lifeos:unauthorized"));
    }
    if (err.response?.status === 402) {
      toast.error(err.response.data?.detail || "Monthly limit reached — upgrade to continue.");
      window.dispatchEvent(new CustomEvent("lifeos:limit"));
    }
    return Promise.reject(err);
  }
);

export const api = {
  register: (payload) => http.post("/auth/register", payload).then((r) => r.data),
  login: (payload) => http.post("/auth/login", payload).then((r) => r.data),
  googleSession: (session_id) => http.post("/auth/google/session", { session_id }).then((r) => r.data),
  me: () => http.get("/auth/me").then((r) => r.data),

  bootstrap: () => http.get("/bootstrap").then((r) => r.data),

  createDepartment: (payload) => http.post("/departments", payload).then((r) => r.data),
  updateDepartment: (id, payload) => http.put(`/departments/${id}`, payload).then((r) => r.data),
  deleteDepartment: (id) => http.delete(`/departments/${id}`).then((r) => r.data),

  hireAgent: (payload) => http.post("/agents", payload).then((r) => r.data),
  updateAgent: (id, payload) => http.put(`/agents/${id}`, payload).then((r) => r.data),
  deleteAgent: (id) => http.delete(`/agents/${id}`).then((r) => r.data),

  runTask: (id, instruction) => http.post(`/agents/${id}/task`, { instruction }).then((r) => r.data),
  getMessages: (id) => http.get(`/agents/${id}/messages`).then((r) => r.data),

  updateIntegration: (id, connected) => http.put(`/integrations/${id}`, { connected }).then((r) => r.data),
  getActivity: (limit = 60) => http.get(`/activity?limit=${limit}`).then((r) => r.data),

  startCollab: (agent_ids, objective) => http.post("/collab", { agent_ids, objective }).then((r) => r.data),
  getScoreboard: () => http.get("/scoreboard").then((r) => r.data),

  checkout: (lookup_key) => http.post("/payments/checkout", { lookup_key, origin_url: window.location.origin }).then((r) => r.data),
  paymentStatus: (sid) => http.get(`/payments/status/${sid}`).then((r) => r.data),
  redeemAtm: (code) => http.post("/atm/redeem", { code }).then((r) => r.data),
  billingSummary: () => http.get("/billing").then((r) => r.data),
  storeServices: () => http.get("/store/services").then((r) => r.data),
  createServiceLink: (key) => http.post(`/store/services/${key}/link`).then((r) => r.data),
  clientSales: () => http.get("/store/sales").then((r) => r.data),

  leadSettings: () => http.get("/leads/settings").then((r) => r.data),
  saveLeadKey: (api_key) => http.post("/leads/settings", { api_key }).then((r) => r.data),
  deleteLeadKey: () => http.delete("/leads/settings").then((r) => r.data),
  leadHunt: (query, limit = 20, find_emails = true, source = "quick") => http.post("/leads/hunt", { query, limit, find_emails, source }).then((r) => r.data),
  listLeads: (status = "") => http.get("/leads", { params: { status } }).then((r) => r.data),
  leadStats: () => http.get("/leads/stats").then((r) => r.data),
  updateLead: (id, patch) => http.patch(`/leads/${id}`, patch).then((r) => r.data),
  deleteLead: (id) => http.delete(`/leads/${id}`).then((r) => r.data),
  leadDraft: (id, offer = "") => http.post(`/leads/${id}/draft`, { offer }).then((r) => r.data),
  leadSend: (id, subject, body) => http.post(`/leads/${id}/send`, { subject, body }).then((r) => r.data),
  leadSchedules: () => http.get("/leads/schedules").then((r) => r.data),
  addLeadSchedule: (query, cadence, limit = 20, find_emails = true) => http.post("/leads/schedules", { query, cadence, limit, find_emails }).then((r) => r.data),
  toggleLeadSchedule: (id) => http.patch(`/leads/schedules/${id}`).then((r) => r.data),
  deleteLeadSchedule: (id) => http.delete(`/leads/schedules/${id}`).then((r) => r.data),

  inboxState: () => http.get("/inbox").then((r) => r.data),
  inboxSaveKey: (api_key) => http.post("/inbox/key", { api_key }).then((r) => r.data),
  inboxAddDomain: (name) => http.post("/inbox/domain", { name }).then((r) => r.data),
  inboxVerify: () => http.post("/inbox/verify").then((r) => r.data),
  inboxRefresh: () => http.get("/inbox/refresh").then((r) => r.data),
  inboxSetFrom: (from_name, from_email) => http.post("/inbox/from", { from_name, from_email }).then((r) => r.data),
  inboxDelete: () => http.delete("/inbox").then((r) => r.data),

  googleStatus: () => http.get("/google/status").then((r) => r.data),
  getGoogleConfig: () => http.get("/google/config").then((r) => r.data),
  setGoogleConfig: (client_id, client_secret) => http.post("/google/config", { client_id, client_secret }).then((r) => r.data),
  gmailLogin: () => http.get("/oauth/gmail/login").then((r) => r.data),
  googleInbox: () => http.get("/google/inbox").then((r) => r.data),
  googleEvents: () => http.get("/google/events").then((r) => r.data),
  draftReply: (agentId, email_id, instruction) => http.post(`/agents/${agentId}/draft-reply`, { email_id, instruction }).then((r) => r.data),
  googleSend: (payload) => http.post("/google/send", payload).then((r) => r.data),
  getDeliverables: () => http.get("/deliverables").then((r) => r.data),
  shareDeliverable: (taskId) => http.post(`/deliverables/${taskId}/share`).then((r) => r.data),
  getShare: (shareId) => http.get(`/share/${shareId}`).then((r) => r.data),

  agentCalendar: (agentId, request) => http.post(`/agents/${agentId}/calendar`, { request }).then((r) => r.data),
  getApprovals: () => http.get("/approvals").then((r) => r.data),
  approveSend: (qid, body) => http.post(`/approvals/${qid}/send`, { body }).then((r) => r.data),
  dismissApproval: (qid) => http.delete(`/approvals/${qid}`).then((r) => r.data),

  getRecipes: () => http.get("/recipes").then((r) => r.data),
  getAutomations: () => http.get("/automations").then((r) => r.data),
  createAutomation: (payload) => http.post("/automations", payload).then((r) => r.data),
  deleteAutomation: (id) => http.delete(`/automations/${id}`).then((r) => r.data),
  runAutomation: (id, payload, otp) => http.post(`/automations/${id}/run`, { payload, otp }).then((r) => r.data),

  getAutomationQueue: () => http.get("/automation-queue").then((r) => r.data),
  approveAutomationQueue: (qid, otp) => http.post(`/automation-queue/${qid}/approve`, { otp }).then((r) => r.data),
  dismissAutomationQueue: (qid) => http.delete(`/automation-queue/${qid}`).then((r) => r.data),
  getWallet: () => http.get("/wallet").then((r) => r.data),

  getRecipeSchedules: () => http.get("/recipe-schedules").then((r) => r.data),
  saveRecipeSchedule: (id, payload) => http.put(`/automations/${id}/schedule`, payload).then((r) => r.data),
  toggleRecipeSchedule: (id, enabled) => http.patch(`/recipe-schedules/${id}`, { enabled }).then((r) => r.data),
  deleteRecipeSchedule: (id) => http.delete(`/recipe-schedules/${id}`).then((r) => r.data),
  getScheduleRuns: (id) => http.get(`/recipe-schedules/${id}/runs`).then((r) => r.data),

  getLogos: () => http.get("/studio/logos").then((r) => r.data),
  deleteLogo: (id) => http.delete(`/studio/logos/${id}`).then((r) => r.data),
  studioFile: (kind, id) => http.get(`/studio/${kind}/${id}/file`, { responseType: "blob" }).then((r) => r.data),
  getStudioBriefs: () => http.get("/studio/briefs").then((r) => r.data),
  startStudioBrief: (payload) => http.post("/studio/briefs", payload).then((r) => r.data),
  answerStudioBrief: (id, answers) => http.post(`/studio/briefs/${id}/answers`, { answers }).then((r) => r.data),
  getDesigns: () => http.get("/studio/designs").then((r) => r.data),
  createDesign: (payload) => http.post("/studio/designs", payload).then((r) => r.data),
  deleteDesign: (id) => http.delete(`/studio/designs/${id}`).then((r) => r.data),
  uploadLogo: async (file, onProgress) => {
    onProgress({ percent: 0, label: "Preparing upload" });
    const { data: upload } = await http.post("/studio/uploads", { name: file.name, size: file.size, content_type: file.type });
    for (let part = 0; part < upload.parts; part++) {
      const chunk = file.slice(part * upload.chunk_size, (part + 1) * upload.chunk_size);
      await http.put(`/studio/uploads/${upload.id}/parts/${part}`, chunk, {
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (event) => onProgress({ percent: Math.round(((part + (event.loaded / chunk.size)) / upload.parts) * 90), label: `Uploading chunk ${part + 1} of ${upload.parts}` }),
      });
    }
    onProgress({ percent: 95, label: "Validating and saving logo" });
    const { data } = await http.post(`/studio/uploads/${upload.id}/complete`);
    onProgress({ percent: 100, label: "Logo saved" });
    return data;
  },

  getConnections: () => http.get("/connections").then((r) => r.data),
  saveConnectionKey: (provider, payload) => http.put(`/connections/${provider}/credentials`, payload).then((r) => r.data),
  removeConnectionKey: (provider) => http.delete(`/connections/${provider}/credentials`).then((r) => r.data),
  verifyConnection: (provider) => http.post(`/connections/${provider}/verify`).then((r) => r.data),

  gmailStatus: () => http.get("/gmail/status").then((r) => r.data),
  gmailConnect: (email, app_password) => http.post("/gmail/connect", { email, app_password }).then((r) => r.data),
  gmailScan: (limit = 25) => http.post("/gmail/scan", { limit }).then((r) => r.data),
  gmailDeepScan: (target = 2000) => http.post("/gmail/deep-scan", { target }).then((r) => r.data),
  gmailClientSync: (categories, per_category = 150) => http.post("/gmail/client-sync", { categories, per_category }).then((r) => r.data),
  gmailClientStatus: () => http.get("/gmail/client-status").then((r) => r.data),
  gmailMessages: (category = "", q = "", page = 0, limit = 30) => http.get("/gmail/messages", { params: { category, q, page, limit } }).then((r) => r.data),
  gmailSearchAll: (query, limit = 40) => http.post("/gmail/search", { query, limit }).then((r) => r.data),
  gmailMessage: (mid) => http.get(`/gmail/messages/${encodeURIComponent(mid)}`).then((r) => r.data),
  gmailSend: (to, subject, body, reply_to = "") => http.post("/gmail/send", { to, subject, body, reply_to }).then((r) => r.data),
  gmailAiAction: (mid, action, prompt = "") => http.post(`/gmail/messages/${encodeURIComponent(mid)}/ai`, { action, prompt }).then((r) => r.data),

  domainStoreStatus: () => http.get("/domains/store-status").then((r) => r.data),
  connectRegistrar: (api_key, secret_api_key) => http.post("/domains/registrar/connect", { api_key, secret_api_key }).then((r) => r.data),
  domainSearch: (query) => http.post("/domains/search", { query }).then((r) => r.data),
  domainOrders: () => http.get("/domains/orders").then((r) => r.data),
  domainCheckout: (domain, tld, origin_url) => http.post("/domains/checkout", { domain, tld, origin_url }).then((r) => r.data),
  domainCheckoutStatus: (sid) => http.get(`/domains/checkout/status/${sid}`).then((r) => r.data),
  gmailDisconnect: () => http.delete("/gmail/disconnect").then((r) => r.data),

  musicSearch: (q) => http.get(`/music/search?q=${encodeURIComponent(q)}`).then((r) => r.data),

  messageCenter: () => http.get("/messages/command-center").then((r) => r.data),
  rememberPerson: (pid) => http.post(`/messages/people/${pid}/remember`).then((r) => r.data),

  instagramStatus: () => http.get("/instagram/status").then((r) => r.data),
  saveMetaConfig: (app_id, app_secret) => http.post("/instagram/config", { app_id, app_secret }).then((r) => r.data),
  instagramLogin: () => http.get("/oauth/instagram/login").then((r) => r.data),
  instagramTest: () => http.get("/instagram/test").then((r) => r.data),
  instagramSync: () => http.post("/instagram/sync").then((r) => r.data),
  instagramThreads: () => http.get("/instagram/threads").then((r) => r.data),
  instagramThread: (pid) => http.get(`/instagram/threads/${pid}`).then((r) => r.data),
  instagramReply: (person_id, text) => http.post("/instagram/reply", { person_id, text }).then((r) => r.data),

  telegramStatus: () => http.get("/telegram/status").then((r) => r.data),
  telegramConnect: (token) => http.post("/telegram/connect", { token }).then((r) => r.data),
  telegramDisconnect: () => http.post("/telegram/disconnect").then((r) => r.data),
  telegramReply: (person_id, text) => http.post("/telegram/reply", { person_id, text }).then((r) => r.data),
  telegramTest: () => http.post("/telegram/test").then((r) => r.data),

  tgUserStatus: () => http.get("/telegram/user/status").then((r) => r.data),
  tgUserBegin: (api_id, api_hash, phone) => http.post("/telegram/user/begin", { api_id, api_hash, phone }).then((r) => r.data),
  tgUserFinish: (code, password) => http.post("/telegram/user/finish", { code, password: password || null }).then((r) => r.data),
  tgUserDisconnect: () => http.post("/telegram/user/disconnect").then((r) => r.data),
  tgUserFeed: () => http.get("/telegram/user/feed").then((r) => r.data),
  tgUserGroupMessages: (gid) => http.get(`/telegram/user/groups/${gid}/messages`).then((r) => r.data),
  tgUserMute: (gid, muted) => http.post(`/telegram/user/groups/${gid}/mute`, { muted }).then((r) => r.data),
  tgUserFavorite: (gid, favorite) => http.post(`/telegram/user/groups/${gid}/favorite`, { favorite }).then((r) => r.data),
  tgUserSync: () => http.post("/telegram/user/sync").then((r) => r.data),
  getInputSessions: () => http.get("/input/sessions").then((r) => r.data),
  createInputSession: () => http.post("/input/sessions").then((r) => r.data),
  getInputSession: (id) => http.get(`/input/sessions/${id}`).then((r) => r.data),
  inputMessage: (id, message, page) => http.post(`/input/sessions/${id}/messages`, { message, page }).then((r) => r.data),
  inputDecision: (sid, mid, approved) => http.post(`/input/sessions/${sid}/actions/${mid}`, { approved }).then((r) => r.data),
  inputPlanDecision: (sid, mid, approved) => http.post(`/input/sessions/${sid}/plan/${mid}`, { approved }).then((r) => r.data),

  vibe: (command) => http.post("/vibe", { command }).then((r) => r.data),

  lifeOverview: () => http.get("/life/overview").then((r) => r.data),
  lifePeople: () => http.get("/life/people").then((r) => r.data),
  lifePerson: (id) => http.get(`/life/people/${id}`).then((r) => r.data),
  lifeInbox: () => http.get("/life/inbox").then((r) => r.data),
  lifeOpportunities: () => http.get("/life/opportunities").then((r) => r.data),
  lifeCommitments: () => http.get("/life/commitments").then((r) => r.data),
  lifeAudit: () => http.get("/life/audit").then((r) => r.data),
  lifeDiscover: () => http.post("/life/discover").then((r) => r.data),
  lifeApprove: (id, draft) => http.post(`/life/inbox/${id}/approve`, { draft }).then((r) => r.data),
  lifeDismiss: (id) => http.post(`/life/inbox/${id}/dismiss`).then((r) => r.data),

  bossState: () => http.get("/boss/state").then((r) => r.data),
  saveBossProfile: (payload) => http.put("/boss/profile", payload).then((r) => r.data),
  saveBossKey: (api_key, model) => http.put("/boss/key", { api_key, model }).then((r) => r.data),
  removeBossKey: () => http.delete("/boss/key").then((r) => r.data),
  verifyBossKey: () => http.post("/boss/key/verify").then((r) => r.data),
  setBossAutopilot: (enabled, interval_minutes) => http.put("/boss/autopilot", { enabled, interval_minutes }).then((r) => r.data),
  bossRun: () => http.post("/boss/run").then((r) => r.data),
  bossDirectives: () => http.get("/boss/directives").then((r) => r.data),
  confirmDirective: (id) => http.post(`/boss/directives/${id}/confirm`).then((r) => r.data),
  dismissDirective: (id) => http.post(`/boss/directives/${id}/dismiss`).then((r) => r.data),
  bossCycles: () => http.get("/boss/cycles").then((r) => r.data),
  bossChatHistory: () => http.get("/boss/chat").then((r) => r.data),
  bossChat: (message) => http.post("/boss/chat", { message }).then((r) => r.data),
  bossBriefing: (refresh = false) => http.get(`/boss/briefing${refresh ? "?refresh=true" : ""}`).then((r) => r.data),

  imessageStatus: () => http.get("/imessage/status").then((r) => r.data),
  imessagePair: (device_name) => http.post("/imessage/pair", { device_name }).then((r) => r.data),
  imessageDisconnect: (deviceId) => http.delete(`/imessage/disconnect${deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : ""}`).then((r) => r.data),
  imessageScan: () => http.post("/imessage/scan").then((r) => r.data),
  imessageNotifications: () => http.get("/imessage/notifications").then((r) => r.data),
  imessageMarkRead: (ids) => http.post("/imessage/notifications/read", ids ? { ids } : {}).then((r) => r.data),
  messageThread: (pid) => http.get(`/messages/thread/${pid}`).then((r) => r.data),
  imessageBrainGet: (pid) => http.get(`/imessage/brain/${pid}`).then((r) => r.data),
  imessageBrainBuild: (pid) => http.post(`/imessage/brain/${pid}`).then((r) => r.data),
  imessageReply: (person_id, text) => http.post("/imessage/reply", { person_id, text }).then((r) => r.data),
  imessageInsights: () => http.get("/imessage/insights").then((r) => r.data),
  imessageDismissInsight: (id) => http.post(`/imessage/insights/${id}/dismiss`).then((r) => r.data),
  imessageAgentScan: () => http.post("/imessage/agent/scan").then((r) => r.data),
  imessageDeepEstimate: (range) => http.get(`/imessage/agent/estimate?range=${encodeURIComponent(range)}`).then((r) => r.data),
  imessageDeepScan: (range) => http.post("/imessage/agent/deep-scan", { range }).then((r) => r.data),
  imessageDeepStatus: () => http.get("/imessage/agent/deep-status").then((r) => r.data),
  assistantStatus: () => http.get("/assistant").then((r) => r.data),
  assistantTiers: () => http.get("/assistant/tiers").then((r) => r.data),
  assistantConfig: (cfg) => http.post("/assistant/config", cfg).then((r) => r.data),
  assistantActivate: (tier) => http.post("/assistant/activate", { tier }).then((r) => r.data),
  assistantRun: () => http.post("/assistant/run").then((r) => r.data),
  assistantRunStatus: () => http.get("/assistant/run-status").then((r) => r.data),
  assistantDrafts: () => http.get("/assistant/drafts").then((r) => r.data),
  assistantApprove: (id, text) => http.post(`/assistant/drafts/${id}/approve`, { text }).then((r) => r.data),
  assistantDismiss: (id) => http.post(`/assistant/drafts/${id}/dismiss`).then((r) => r.data),
  assistantApproveAll: (edits) => http.post("/assistant/approve-all", { edits }).then((r) => r.data),
  assistantRenameContact: (pid, name) => http.post(`/assistant/contact/${pid}/name`, { name }).then((r) => r.data),
  uploadContacts: (vcard) => http.post("/imessage/contacts", { vcard }).then((r) => r.data),
  uploadChatDb: async (file, onProgress) => {
    onProgress?.({ percent: 0, label: "Preparing" });
    const { data: up } = await http.post("/imessage/upload/init", { size: file.size });
    for (let part = 0; part < up.parts; part++) {
      const chunk = file.slice(part * up.chunk_size, (part + 1) * up.chunk_size);
      await http.put(`/imessage/upload/${up.id}/parts/${part}`, chunk, { headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (e) => onProgress?.({ percent: Math.round(((part + e.loaded / (chunk.size || 1)) / up.parts) * 90), label: `Uploading ${part + 1}/${up.parts}` }) });
    }
    onProgress?.({ percent: 95, label: "Reading your history" });
    const { data } = await http.post(`/imessage/upload/${up.id}/complete`);
    onProgress?.({ percent: 100, label: "Done" });
    return data;
  },

  bossKnowledge: () => http.get("/boss/knowledge").then((r) => r.data),
  getBossInterview: () => http.get("/boss/interview").then((r) => r.data),
  submitBossInterview: (answers) => http.post("/boss/interview", { answers }).then((r) => r.data),
  bossQuestions: () => http.get("/boss/questions").then((r) => r.data),
  answerBossQuestion: (id, answer) => http.post(`/boss/questions/${id}/answer`, { answer }).then((r) => r.data),
  dismissBossQuestion: (id) => http.post(`/boss/questions/${id}/dismiss`).then((r) => r.data),
  bossRecommendations: () => http.get("/boss/recommendations").then((r) => r.data),
  acceptBossRecommendation: (id) => http.post(`/boss/recommendations/${id}/accept`).then((r) => r.data),
  dismissBossRecommendation: (id) => http.post(`/boss/recommendations/${id}/dismiss`).then((r) => r.data),
  uploadChatExport: async (file, onProgress) => {
    onProgress?.({ percent: 0, label: "Preparing" });
    const { data: up } = await http.post("/boss/knowledge/upload/init", { name: file.name, size: file.size, content_type: file.type });
    for (let part = 0; part < up.parts; part++) {
      const chunk = file.slice(part * up.chunk_size, (part + 1) * up.chunk_size);
      await http.put(`/boss/knowledge/upload/${up.id}/parts/${part}`, chunk, { headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (e) => onProgress?.({ percent: Math.round(((part + e.loaded / (chunk.size || 1)) / up.parts) * 90), label: `Uploading ${part + 1}/${up.parts}` }) });
    }
    onProgress?.({ percent: 95, label: "LifeOS is reading your chats" });
    const { data } = await http.post(`/boss/knowledge/upload/${up.id}/complete`);
    onProgress?.({ percent: 100, label: "Done" });
    return data;
  },
};

export async function streamChat(agentId, message, onDelta, onDone) {
  const res = await fetch(`${API}/agents/${agentId}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ message }),
  });
  if (res.status === 402) {
    toast.error("Monthly limit reached — upgrade to continue.");
    window.dispatchEvent(new CustomEvent("lifeos:limit"));
    if (onDone) onDone();
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n\n");
    buffer = lines.pop() || "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) continue;
      try {
        const payload = JSON.parse(trimmed.slice(5).trim());
        if (payload.delta) onDelta(payload.delta);
        if (payload.done && onDone) onDone();
      } catch (e) {}
    }
  }
  if (onDone) onDone();
}
